// gridEvent.ts — ein einzelner Graph-Termin als `CalendarGridEvent`.
//
// Das Raster baut seine Events beim Listen-Load (CalendarStateContext); wer
// EINEN Termin zeigen will — etwa der Peek auf der Termin-Notizen-Seite oder im
// Vorgang — hat statt der Liste ein `getEvent`-Ergebnis. Dieselbe Abbildung
// zweimal zu schreiben hieße, dass die beiden Ansichten auseinanderlaufen
// (Ganztags-Datum, Serien-Indikator, Teams-Link hängen alle daran).
//
// Bewusst ohne React/DOM → unter vitest (node) testbar.

import type { CalendarEvent } from "../../services/calendarService";
import { toGraphDate } from "../../utils/graphDatum";
import { graphAllDayDate } from "./allDay";
import type { CalendarGridEvent, CalendarMeta } from "./types";

/**
 * Graph-Termin → Raster-Event.
 *
 * `mailbox` ist das Postfach, aus dem der Termin geladen wurde — daran hängt
 * das Schreibrecht. `meta` ist die Sidecar-Klassifikation (Art, Farbe,
 * verknüpfter Vorgang); ohne sie bleibt der Termin unklassifiziert, was die
 * Anzeige verträgt.
 */
export function gridEventAusTermin(
    ev: CalendarEvent,
    mailbox: string,
    meta?: CalendarMeta | null,
): CalendarGridEvent {
    const allDay = ev.isAllDay || false;
    return {
        id: ev.id,
        title: ev.subject,
        // Ganztagstermine liefert Graph als Mitternacht UTC — nur den
        // Datumsteil als LOKALE Mitternacht nehmen, sonst spannt der Termin
        // einen Tag zu weit (dieselbe Regel wie im Listen-Load).
        start: allDay ? graphAllDayDate(ev.start.dateTime) : toGraphDate(ev.start.dateTime),
        end: allDay ? graphAllDayDate(ev.end.dateTime) : toGraphDate(ev.end.dateTime),
        allDay,
        location: ev.location || "",
        mailbox,
        // Ein einzeln geladener Termin kennt keine Quellen-Farbe der Kalender-
        // Auswahl; die Anzeige fällt auf den Akzent bzw. die Meta-Farbe zurück.
        source: { address: mailbox, displayName: mailbox, color: "" },
        meta: meta ?? undefined,
        type: ev.type,
        seriesMasterId: ev.seriesMasterId,
        showAs: ev.showAs ?? null,
        sensitivity: ev.sensitivity ?? null,
        onlineMeetingUrl: ev.onlineMeetingUrl ?? null,
        iCalUId: ev.iCalUId ?? null,
        organizer: ev.organizer ?? null,
        attendees: ev.attendees ?? [],
        bodyPreview: ev.bodyPreview ?? "",
    };
}
